import { useEffect, useState, useContext } from 'react';
import api from '../api/api';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Select, MenuItem, InputLabel, FormControl, Chip, Divider
} from '@mui/material';
import { DataGrid, GridToolbarExport, GridToolbarContainer } from '@mui/x-data-grid';
import { AuthContext } from '../context/AuthContext';

const roleColors = {
  admin: { backgroundColor: '#163244', color: '#E7A432' },
  user:  { backgroundColor: '#f0f4f8', color: '#163244' },
};

const AdminUsersPage = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [unlockDialog, setUnlockDialog] = useState({ open: false, user: null });
  const [formData, setFormData] = useState({ id: null, name: '', email: '', role: '', phone: '', address: '', dni: '', birthdate: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // DataGrid v8: el modelo de selección es { type: 'include'|'exclude', ids: Set }
  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error('Error cargando usuarios:', err);
      setError(err.response?.data?.error || 'No se pudieron cargar los usuarios. Verificá tu sesión.');
    } finally {
      setLoading(false);
    }
  };

  // Extrae array de IDs del modelo v8
  const selectedIds = selectionModel.type === 'include'
    ? [...selectionModel.ids]
    : users.map(u => u.id).filter(id => !selectionModel.ids.has(id));

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter ? u.role === roleFilter : true;
    return matchesSearch && matchesRole;
  });

  const handleOpenDialog = (user = null) => {
    setFormData(user
      ? {
          id: user.id,
          name: user.name || '',
          email: user.email || '',
          role: user.role || 'user',
          phone: user.phone || '',
          address: user.address || '',
          dni: user.dni || '',
          birthdate: user.birthdate ? user.birthdate.substring(0, 10) : ''
        }
      : { id: null, name: '', email: '', role: 'user', phone: '', address: '', dni: '', birthdate: '' }
    );
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      setSnackbar({ open: true, message: 'Completá todos los campos.', severity: 'warning' });
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await api.put(`/users/admin-op/${formData.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(prev => prev.map(u => u.id === formData.id ? res.data : u));
      setSnackbar({ open: true, message: 'Usuario actualizado correctamente.', severity: 'success' });
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al guardar.', severity: 'error' });
    }
  };

  const handleUnlock = async () => {
    try {
      const res = await api.post(`/users/admin-op/unlock/${unlockDialog.user.id}`);
      setUsers(prev => prev.map(u =>
        u.id === unlockDialog.user.id
          ? { ...u, failed_attempts: 0, locked_until: null }
          : u
      ));
      setSnackbar({ open: true, message: res.data.message, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al desbloquear.', severity: 'error' });
    } finally {
      setUnlockDialog({ open: false, user: null });
    }
  };

  const handleResetPassword = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await api.post(`/users/admin-op/reset-password/${resetDialog.user.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: res.data.message, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al restablecer la contraseña.', severity: 'error' });
    } finally {
      setResetDialog({ open: false, user: null });
    }
  };

  const handleDeleteConfirmed = async () => {
    const token = localStorage.getItem('token');
    const ids = deleteDialog.ids;
    let errored = false;
    for (const id of ids) {
      try {
        await api.delete(`/users/admin-op/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error eliminando usuario', id, err);
        errored = true;
      }
    }
    setUsers(prev => prev.filter(u => !ids.includes(u.id)));
    setSelectionModel({ type: 'include', ids: new Set() });
    setDeleteDialog({ open: false, ids: [] });
    setSnackbar({
      open: true,
      message: errored ? 'Algunos usuarios no pudieron eliminarse.' : `${ids.length} usuario(s) eliminado(s).`,
      severity: errored ? 'warning' : 'success'
    });
  };

  const columns = [
    { field: 'name', headerName: 'Nombre', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.2 },
    {
      field: 'role', headerName: 'Rol', flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value === 'admin' ? 'Admin' : 'Usuario'}
          size="small"
          sx={{ ...roleColors[params.value], fontWeight: 600, fontSize: '0.75rem' }}
        />
      )
    },
    {
      field: 'created_at', headerName: 'Registro', flex: 0.8,
      renderCell: (params) => params.value
        ? new Date(params.value).toLocaleDateString('es-AR')
        : '—'
    },
    {
      field: 'locked_until', headerName: 'Estado', flex: 0.8,
      renderCell: (params) => {
        const isLocked = params.value && new Date() < new Date(params.value);
        if (isLocked) {
          const mins = Math.ceil((new Date(params.value) - new Date()) / 60000);
          return (
            <Chip
              label={`🔒 Bloqueado (${mins}m)`}
              size="small"
              sx={{ backgroundColor: '#fdecea', color: '#c0392b', fontWeight: 600, fontSize: '0.72rem' }}
            />
          );
        }
        return (
          <Chip
            label="✓ Activo"
            size="small"
            sx={{ backgroundColor: '#e8f7ef', color: '#1b7f4e', fontWeight: 600, fontSize: '0.72rem' }}
          />
        );
      }
    },
    {
      field: 'actions', headerName: 'Acciones', flex: 1.4, sortable: false,
      renderCell: (params) => {
        const isLocked = params.row.locked_until && new Date() < new Date(params.row.locked_until);
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small" variant="outlined"
              sx={{ fontSize: '0.7rem', borderColor: '#163244', color: '#163244' }}
              onClick={() => handleOpenDialog(params.row)}
            >
              Editar
            </Button>
            {isLocked && (
              <Button
                size="small" variant="outlined"
                sx={{ fontSize: '0.7rem', borderColor: '#1b7f4e', color: '#1b7f4e' }}
                onClick={() => setUnlockDialog({ open: true, user: params.row })}
              >
                Desbloquear
              </Button>
            )}
            <Button
              size="small" variant="outlined"
              sx={{ fontSize: '0.7rem', borderColor: '#854F0B', color: '#854F0B' }}
              disabled={params.row.id === currentUser?.id}
              onClick={() => setResetDialog({ open: true, user: params.row })}
            >
              Reset
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              sx={{ fontSize: '0.7rem' }}
              disabled={params.row.id === currentUser?.id}
              onClick={() => setDeleteDialog({ open: true, ids: [params.row.id] })}
            >
              Eliminar
            </Button>
          </Box>
        );
      }
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: 'usuarios_fixo' }} />
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#163244' }}>
        Administración de Usuarios
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Buscar por nombre o email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Rol</InputLabel>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} label="Rol">
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="admin">Administrador</MenuItem>
            <MenuItem value="user">Usuario</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" color="error"
            disabled={selectedIds.length === 0}
            onClick={() => setDeleteDialog({ open: true, ids: selectedIds })}
          >
            Eliminar seleccionados ({selectedIds.length})
          </Button>
          <Button
            variant="contained"
            onClick={fetchUsers}
            sx={{ backgroundColor: '#163244', color: '#fff' }}
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataGrid
        rows={filteredUsers}
        columns={columns}
        autoHeight
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        getRowId={(row) => row.id}
        slots={{ toolbar: CustomToolbar }}
        sx={{
          borderRadius: 2,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#163244',
            color: '#fff',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
        }}
        localeText={{
          noRowsLabel: 'No hay usuarios',
          footerRowSelected: (count) => `${count} usuario(s) seleccionado(s)`,
        }}
      />

      {/* Dialog editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#163244', color: '#fff' }}>Editar Usuario</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, mt: 0.5 }}>
            Datos de acceso
          </Typography>
          <TextField
            label="Nombre completo" fullWidth margin="dense"
            value={formData.name}
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
          />
          <TextField
            label="Email" fullWidth margin="dense"
            value={formData.email}
            onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Rol</InputLabel>
            <Select value={formData.role} label="Rol"
              onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="user">Usuario</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Datos personales
          </Typography>

          <TextField
            label="Teléfono" fullWidth margin="dense"
            value={formData.phone}
            onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
          />
          <TextField
            label="Dirección" fullWidth margin="dense"
            value={formData.address}
            onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
          />
          <TextField
            label="DNI / CUIT" fullWidth margin="dense"
            value={formData.dni}
            onChange={(e) => setFormData(p => ({ ...p, dni: e.target.value }))}
          />
          <TextField
            label="Fecha de nacimiento" fullWidth margin="dense"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.birthdate}
            onChange={(e) => setFormData(p => ({ ...p, birthdate: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined"
            sx={{ borderColor: '#163244', color: '#163244' }}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained"
            sx={{ backgroundColor: '#E7A432', color: '#163244', fontWeight: 600 }}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminación */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, ids: [] })}>
        <DialogTitle>¿Confirmar eliminación?</DialogTitle>
        <DialogContent>
          <Typography>
            Vas a eliminar {deleteDialog.ids.length} usuario(s). Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, ids: [] })} variant="outlined">Cancelar</Button>
          <Button onClick={handleDeleteConfirmed} variant="contained" color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar reset contraseña */}
      <Dialog open={resetDialog.open} onClose={() => setResetDialog({ open: false, user: null })}>
        <DialogTitle>¿Restablecer contraseña?</DialogTitle>
        <DialogContent>
          <Typography>
            Se generará una contraseña temporal para <strong>{resetDialog.user?.name}</strong> y se enviará
            a <strong>{resetDialog.user?.email}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El usuario deberá cambiarla después de iniciar sesión.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog({ open: false, user: null })} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleResetPassword} variant="contained"
            sx={{ backgroundColor: '#854F0B', color: '#fff', '&:hover': { backgroundColor: '#633806' } }}>
            Restablecer y enviar email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar desbloqueo */}
      <Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false, user: null })}>
        <DialogTitle>¿Desbloquear cuenta?</DialogTitle>
        <DialogContent>
          <Typography>
            Vas a desbloquear la cuenta de <strong>{unlockDialog.user?.name}</strong>.
            Los intentos fallidos se resetearán y el usuario podrá iniciar sesión nuevamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, user: null })} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleUnlock} variant="contained"
            sx={{ backgroundColor: '#1b7f4e', color: '#fff', '&:hover': { backgroundColor: '#145c38' } }}>
            Desbloquear
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;